import { inr, num } from "@/lib/api";

const title = (k) => k.replace(/_/g, " ").replace(/\binr\b/i, "(INR)").replace(/\b\w/g, (c) => c.toUpperCase());
const isPrim = (v) => v == null || ["string", "number", "boolean"].includes(typeof v);

function fmt(k, v) {
  if (typeof v === "number") {
    if (/inr|budget|cost|revenue|amount|price/.test(k)) return inr(v);
    if (/pct|share/.test(k)) return `${v}%`;
    return num(v);
  }
  return String(v ?? "-");
}

function Score({ k, v }) {
  const color = v >= 75 ? "bg-emerald-500" : v >= 50 ? "bg-cyan-500" : v >= 30 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="min-w-[120px]" data-testid={`report-score-${k}`}>
      <div className="flex justify-between text-xs mb-1"><span className="text-slate-400">{title(k)}</span><span className="font-mono font-bold">{v}</span></div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden"><div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${Math.min(100, v)}%` }} /></div>
    </div>
  );
}

function Cell({ k, v }) {
  if (Array.isArray(v)) return <span>{v.join(", ")}</span>;
  if (typeof v === "number" && /score/.test(k)) return <Score k={k} v={v} />;
  return <span className={typeof v === "number" ? "font-mono" : ""}>{fmt(k, v)}</span>;
}

function objList(k, items) {
  const keys = [...new Set(items.flatMap((i) => Object.keys(i)))];
  const flat = keys.length <= 7 && items.every((i) => Object.values(i).every((v) => isPrim(v) || (Array.isArray(v) && v.every(isPrim) && v.length <= 4)));
  if (flat) {
    return (
      <div className="overflow-x-auto custom-scrollbar rounded-lg border border-slate-800">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-slate-900/70 text-[11px] uppercase tracking-wider text-slate-400"><tr>{keys.map((c) => <th key={c} className="px-3 py-2 text-left whitespace-nowrap">{title(c)}</th>)}</tr></thead>
          <tbody>{items.map((i, idx) => <tr key={idx} className="border-t border-slate-800/70">{keys.map((c) => <td key={c} className="px-3 py-2 align-top text-slate-300"><Cell k={c} v={i[c]} /></td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {items.map((i, idx) => <div key={idx} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">{renderNode(i, 2)}</div>)}
    </div>
  );
}

function renderNode(data, depth = 0) {
  const entries = Object.entries(data || {});
  const scores = entries.filter(([k, v]) => typeof v === "number" && /score/.test(k));
  return (
    <div className="space-y-4">
      {scores.length > 0 && <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{scores.map(([k, v]) => <Score key={k} k={k} v={v} />)}</div>}
      {entries.filter(([k, v]) => !(typeof v === "number" && /score/.test(k))).map(([k, v]) => {
        if (isPrim(v)) {
          const long = typeof v === "string" && v.length > 60;
          return (
            <div key={k} className={long ? "" : "flex justify-between gap-4 border-b border-slate-800/60 pb-1.5"}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{title(k)}</div>
              <div className={`text-sm text-slate-200 ${long ? "mt-1 leading-relaxed" : "text-right font-medium"}`}>{fmt(k, v)}</div>
            </div>
          );
        }
        const H = depth === 0 ? "text-base font-bold text-slate-100" : "text-sm font-semibold text-slate-200";
        return (
          <div key={k} className={depth === 0 ? "lx-card" : ""}>
            <div className={`${H} mb-3`}>{title(k)}</div>
            {Array.isArray(v) ? (
              v.every(isPrim) ? (
                <ul className="space-y-1.5">{v.map((x, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-emerald-400 mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />{String(x)}</li>)}</ul>
              ) : objList(k, v)
            ) : renderNode(v, depth + 1)}
          </div>
        );
      })}
    </div>
  );
}

export default function ReportRenderer({ data }) {
  if (!data) return null;
  const { summary, headline, ...rest } = data;
  return (
    <div className="space-y-5 fade-up" data-testid="report-renderer">
      {(summary || headline) && (
        <div className="lx-card border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-transparent">
          <div className="lx-ai-badge mb-2">AI Summary</div>
          <p className="text-sm sm:text-base text-slate-200 leading-relaxed">{headline || summary}</p>
        </div>
      )}
      {renderNode(rest)}
    </div>
  );
}
